'use client';

import { useState, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faLayerGroup, faTag, faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { AnimateOnView } from "@/components/ui/AnimateOnView";
import { EmptyState } from "@/components/ui/EmptyState";
import CollectionsSearchBar from "@/components/ui/CollectionsSearchBar";
import { Collection } from "@/lib/collections";

interface CollectionsExplorerProps {
  collections: Collection[];
  currentPage?: number;
  totalPages?: number;
  totalCollections?: number;
  initialSearch?: string;
}

const FALLBACK_TILE = "/images/collections/colec1/4d2dc65da292720fc5dabd9b5e10b97e.jpg";

function buildPreviewTiles(previews: string[] = [], fallback?: string | null): string[] {
  const sanitized = previews.filter(Boolean);

  if (sanitized.length >= 4) {
    return sanitized.slice(0, 4);
  }

  const tiles = [...sanitized];
  const fallbackImage = fallback || sanitized[0] || FALLBACK_TILE;

  while (tiles.length < 4) {
    tiles.push(fallbackImage);
  }

  return tiles.slice(0, 4);
}

function CollectionCard({ collection }: { collection: Collection }) {
  const previewTiles = buildPreviewTiles(collection.previews ?? [], collection.cover);

  return (
    <Link
      href={`/colecoes/${collection.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-white/10 bg-white/5 p-2 text-left transition-transform hover:-translate-y-1 hover:border-white/20 focus-visible:-translate-y-1 focus-visible:border-brand-secondary/60 focus-visible:outline-none"
    >
      <div className="relative overflow-hidden rounded-lg border border-white/5 bg-[#120226]/80 shadow-[0_15px_40px_-20px_rgba(28,0,74,0.8)]">
        <div className="grid grid-cols-2 gap-px">
          {previewTiles.map((preview, index) => (
            <div key={`${collection.id}-preview-${index}`} className="relative aspect-square overflow-hidden">
              <Image
                src={preview}
                alt={`Prévia da coleção ${collection.title}`}
                fill
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 200px"
                className="object-cover transition duration-500 group-hover:scale-105"
                unoptimized
              />
              <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </div>
          ))}
        </div>
        <div className="pointer-events-none absolute inset-0 flex items-end justify-between gap-1.5 p-1.5">
          <span className="rounded-full bg-black/70 px-1.5 py-0.5 text-[7px] font-semibold uppercase tracking-wider text-white">
            {collection.assetsCount}
          </span>
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-brand-secondary/30 text-brand-secondary">
            <FontAwesomeIcon icon={faArrowRight} className="h-2.5 w-2.5" />
          </span>
        </div>
      </div>

      <div className="mt-2 space-y-0.5">
        <h3 className="text-xs font-semibold text-white line-clamp-1">{collection.title}</h3>
        <p className="text-[10px] text-white/50 line-clamp-1">{collection.description}</p>
      </div>
    </Link>
  );
}

export default function CollectionsExplorer({ 
  collections, 
  currentPage = 1, 
  totalPages = 1,
  totalCollections = 0,
  initialSearch = ''
}: CollectionsExplorerProps) {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Navega para a URL com o termo de busca (busca server-side via API)
  const performSearch = useCallback((term: string) => {
    setIsSearching(true);
    const params = new URLSearchParams();
    if (term.trim()) {
      params.set('search', term.trim());
    }
    params.set('page', '1');
    router.push(`/colecoes?${params.toString()}`);
    // isSearching será resetado quando a página re-renderizar com novos dados
    setTimeout(() => setIsSearching(false), 500);
  }, [router]);

  // Handler para mudança na busca com debounce
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(value);
    }, 500);
  }, [performSearch]);

  // Limpa debounce ao desmontar
  const filteredCollections = collections;

  return (
    <div className="flex flex-col gap-8 pb-10">
      <AnimateOnView animation="animate__fadeInUp" className="flex flex-col gap-6">
        {/* Barra de Pesquisa - Centralizada */}
        <div className="flex justify-center">
          <CollectionsSearchBar
            value={searchQuery}
            onChange={handleSearchChange}
            resultsCount={searchQuery.trim() ? filteredCollections.length : undefined}
            isSearching={isSearching}
            placeholder="Pesquisar coleções por nome ou palavra-chave..."
          />
        </div>

        {filteredCollections.length === 0 ? (
          <EmptyState
            icon={faLayerGroup}
            title="Nenhuma coleção encontrada"
            description={searchQuery.trim() 
              ? `Não encontramos coleções para "${searchQuery}". Tente outro termo ou verifique a ortografia.`
              : "Remova filtros ou ajuste a busca para visualizar novas composições."
            }
          />
        ) : (
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4">
            {filteredCollections.map((collection) => (
              <CollectionCard key={collection.id} collection={collection} />
            ))}
          </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2">
            {/* Botão Anterior */}
            <Link
              href={currentPage > 1 ? `/colecoes?page=${currentPage - 1}${searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery.trim())}` : ''}` : '#'}
              className={clsx(
                "flex h-10 w-10 items-center justify-center rounded-full border transition",
                currentPage > 1
                  ? "border-white/20 bg-white/5 text-white hover:border-brand-secondary hover:bg-brand-secondary/20 hover:text-brand-secondary"
                  : "pointer-events-none border-white/10 bg-white/5 text-white/30"
              )}
              aria-disabled={currentPage <= 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" />
            </Link>

            {/* Números das páginas */}
            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                // Mostrar: primeira, última, atual e adjacentes
                const showPage = 
                  pageNum === 1 || 
                  pageNum === totalPages || 
                  Math.abs(pageNum - currentPage) <= 1;
                
                // Mostrar reticências
                const showEllipsisBefore = pageNum === currentPage - 2 && currentPage > 3;
                const showEllipsisAfter = pageNum === currentPage + 2 && currentPage < totalPages - 2;

                if (showEllipsisBefore || showEllipsisAfter) {
                  return (
                    <span key={`ellipsis-${pageNum}`} className="px-2 text-white/40">
                      ...
                    </span>
                  );
                }

                if (!showPage) return null;

                return (
                  <Link
                    key={pageNum}
                    href={`/colecoes?page=${pageNum}${searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery.trim())}` : ''}`}
                    className={clsx(
                      "flex h-10 min-w-[40px] items-center justify-center rounded-full border px-3 text-sm font-medium transition",
                      pageNum === currentPage
                        ? "border-brand-secondary bg-brand-secondary/20 text-brand-secondary"
                        : "border-white/10 bg-white/5 text-white/70 hover:border-white/20 hover:text-white"
                    )}
                  >
                    {pageNum}
                  </Link>
                );
              })}
            </div>

            {/* Botão Próximo */}
            <Link
              href={currentPage < totalPages ? `/colecoes?page=${currentPage + 1}${searchQuery.trim() ? `&search=${encodeURIComponent(searchQuery.trim())}` : ''}` : '#'}
              className={clsx(
                "flex h-10 w-10 items-center justify-center rounded-full border transition",
                currentPage < totalPages
                  ? "border-white/20 bg-white/5 text-white hover:border-brand-secondary hover:bg-brand-secondary/20 hover:text-brand-secondary"
                  : "pointer-events-none border-white/10 bg-white/5 text-white/30"
              )}
              aria-disabled={currentPage >= totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" />
            </Link>
          </div>
        )}

      </AnimateOnView>
    </div>
  );
}
